/// <reference types="@types/googlemaps" />
import { Component, Input, OnInit, ViewChild, NgZone, ElementRef } from '@angular/core';
import { MatDialogRef } from '@angular/material';
import { from as fromPromise } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { VolvoApiService } from '../../../../services/volvo-api.service';
import { GoogleMapsService } from '../../services/google-maps.service';
import { PageConfiguration } from '../../../../models/page-configuration';
import MarkerClusterer from '@googlemaps/markerclustererplus';

@Component({
  selector: 'app-find-participating-dealer-map',
  templateUrl: './find-participating-dealer-map.component.html',
  styleUrls: ['./find-participating-dealer-map.component.scss']
})
export class FindParticipatingDealerMapComponent implements OnInit {

  @Input() Guid: string;
  @Input() localStrings: Array<any>;
  @Input() pageConfig: PageConfiguration;
  isLoading = true;
  isSearching = false;
  hasSearched = false;
  dealers: any[];

  nearbyDealers: any = [];
  map: google.maps.Map;
  dealerMarkers: google.maps.Marker[];
  bounds: google.maps.LatLngBounds;
  cluster: any;
  openInfoWindow: any;
  searchAddress: string;

  @ViewChild('gmap') gmapElement: any;
  @ViewChild('addressInput') searchElement: ElementRef;
  @ViewChild('mapSearchForm') searchForm: ElementRef;

  constructor(
    private volvoApi: VolvoApiService,
    private gmService: GoogleMapsService,
    private dialogRef: MatDialogRef<FindParticipatingDealerMapComponent>,
    private ngZone: NgZone
    
  ) { }

  ngOnInit() {
    fromPromise(this.gmService.load())
      .pipe(switchMap(() => this.volvoApi.getPromotionPartners(this.Guid)))
      .subscribe(dealers => {
        this.dealers = dealers;
        this.initGoogleMap();
        this.initAutoComplete();
        this.addDealerMarkers(this.dealers);
        this.isLoading = false;
      });
  }

  initAutoComplete() {

    const CountryID = this.pageConfig.CountryID;
    const countryCode = (CountryID === 309) ? 'ca' : (CountryID === 271) ? 'us' : 'us';
    const autocompleteOptions = {
      componentRestrictions: { country: [ countryCode ] } ,
    };
    // assign autocomplete function to search input
    const autocomplete = new google.maps.places.Autocomplete(this.searchElement.nativeElement, autocompleteOptions);

    // listen for event where place changed to display options
    autocomplete.addListener('place_changed', () => {
      this.ngZone.run(() => {
       const place: google.maps.places.PlaceResult = autocomplete.getPlace();
       if (place.geometry === undefined || place.geometry === null ) {
        return;
       }

      });
    });
  }

  initGoogleMap() {
    this.map = new google.maps.Map(this.gmapElement.nativeElement, Object.assign(this.gmService.baseSettings, {}));
  }

  addDealerMarkers(dealers: any) {
    if (this.cluster) {
      this.cluster.clearMarkers();
    }

    this.dealerMarkers = [];
    this.bounds = new google.maps.LatLngBounds();
    const dealerIcon = 'https://aplanbyvolvo.azureedge.net/media/av/media/aplan/map-markers/mapmarker_dealer_50x50_bk.png';

    for (const dealer of dealers) {
      const markerLatLng = new google.maps.LatLng(dealer.Latitude, dealer.Longitude);
      const marker = new google.maps.Marker({position: markerLatLng, map: this.map, icon: dealerIcon});

      const infoWindow = new google.maps.InfoWindow({
        content: this.createInfoWindowContent(dealer)
      });

      marker.addListener('click', () => {
        this.openInfoWindow ? this.openInfoWindow.close() : 0;
        this.openInfoWindow = infoWindow;
        infoWindow.open(this.map, marker);
      });

      dealer.marker = marker;
      dealer.infoWindow = infoWindow;
      this.dealerMarkers.push(marker);
      this.bounds.extend(markerLatLng);
    }

    const clusterSettings = {
      gridSize: 10,
      maxZoom: 15,
      imagePath: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m'
    };
    this.cluster = new MarkerClusterer(this.map, this.dealerMarkers, clusterSettings);

    this.centerMapAndZoom(this.bounds);
  }

  createInfoWindowContent(dealer: any): string {
    const parsedCompanyLink = dealer.CompanyWeb
      ? (dealer.CompanyWeb.indexOf('http') > -1 ? dealer.CompanyWeb : `http://${dealer.CompanyWeb}`)
      : '';
    const parsedInventoryLink = dealer.InventoryURL
      ? (dealer.InventoryURL.indexOf('http') > -1 ? dealer.InventoryURL : `http://${dealer.InventoryURL}`)
      : '';
    const dealerName = parsedCompanyLink ? '<a href="' + parsedCompanyLink + '" target="_blank">' + dealer.Name + '</a>' : dealer.Name;
    let html = `<b>${dealerName}</b>`;
    html += '<p>';
    html += dealer.City ? `<br><b>City</b>: ${dealer.City}` : '';
    html += dealer.State ? `<br><b>State</b>: ${dealer.State}` : '';
    html += dealer.Phone ? `<br><b>Phone</b>: ${dealer.Phone}` : '';
    html += dealer.parsedInventoryLink ? `<br><a href="${dealer.parsedInventoryLink}" target="_blank">Inventory</a>` : '';
    html += '</p>';
    return html;
  }

  centerMapAndZoom(bounds: google.maps.LatLngBounds): void {
    this.map.fitBounds(bounds);
    this.map.panToBounds(bounds);
  }

  getDealerAddressConcat(dealer: any): string {
    let str = dealer.Address ? `${dealer.Address}, ` : '';
    str += dealer.City ? `${dealer.City}, ` : '';
    str += dealer.State ? `${dealer.State}, ` : '';
    str += dealer.Zipcode ? `${dealer.Zipcode}, ` : '';

    return str;
  }

  async searchBy() {
    const search = this.searchElement.nativeElement.value;

    if (search != '' && search != null) {
      this.isLoading = true;
      this.isSearching = true;
      const geocoder = new google.maps.Geocoder();
      await geocoder.geocode({ address: search }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK) {
          if (results[0].geometry.bounds) {
            this.centerMapAndZoom(results[0].geometry.bounds);
          }
          if (results[0].geometry.location) {
            this.findNearbyDealers(results[0].geometry.location);
          }
        }
        this.searchAddress = search;
        this.hasSearched = true;
        this.isSearching = false;
        this.isLoading = false;
      });
    }
  }

  clearResult() {
    this.nearbyDealers = [];
    this.hasSearched = false;
    this.searchAddress = '';
  }

  zoomToDealer(dealer: any): void {
    this.centerMapAndZoom(new google.maps.LatLngBounds().extend(dealer.marker.getPosition()));
    this.openInfoWindow ? this.openInfoWindow.close() : 0;
    this.openInfoWindow = dealer.infoWindow;
    dealer.infoWindow.open(this.map, dealer.marker);
  }

  findNearbyDealers(location: any): void {
    this.nearbyDealers = [];

    const withinArea = [];
    for (const dealer of this.dealers) {

      let distance = google.maps.geometry.spherical.computeDistanceBetween(dealer.marker.getPosition(), location);
      distance = distance / 1000; // Make meter to KM
      distance = distance / 1.60934; // Make KM to Miles

      if (distance <= 100) {
        withinArea.push(Object.assign({}, dealer, {
          distance: Math.round(distance)
        }));
      }
    }

    withinArea.sort((a, b) => a.distance - b.distance);

    this.nearbyDealers = [ ...withinArea ];
  }
}
